import React, { useState, useEffect } from 'react';
import ChatHeader, { ChatHeaderProps } from '../ChatHeader';
import ChatFooter, { ChatFooterProps } from '../ChatFooter';
import ChatBody, { ChatBodyProps } from '../ChatBody';
import Form from '../Form';
import ChatAPI, { Chat as ChatType, SD_ANONYMOUS_USER_ID, setGlobal } from '../../chat-api';
import { useAppState, useChatState, useTheme } from '../../data';
import {
  Container,
  Content,
  StyledCollapse,
  LoadingChat,
  StartMessages,
  MessageSkeleton,
  EndMessages,
} from './styles';
import { getGlobal } from '../../chat-api';

export interface ChatProps {
  horizontalPosition?: 'left' | 'center' | 'right' | 'custom';
  customPosition?: {
    right?: number,
    left?: number
  };
  header?: ChatHeaderProps;
  body?: ChatBodyProps;
  footer?: ChatFooterProps;
}

const Chat: React.FC<ChatProps> = () => {
  const chatAPI = new ChatAPI();
  const {
    isLoading,
    isAnonymous,
    showAvatar,
    responsiveMode
  } = useAppState();
  const {
    isMobile,
    setIsMobile,
    isExpanded,
    showChat,
    user,
    setUser,
    setChat,
    setAvatarURL,
    avatarURL
  } = useChatState();
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const resizeCallback = () => {
      document.querySelector<HTMLElement>(':root')!.style
        .setProperty('--vh', `${window.innerHeight / 100}px`);
    }

    resizeCallback();
    window.addEventListener('resize', resizeCallback);
    return () => {
      window.removeEventListener('resize', resizeCallback);
    }
  }, [setIsMobile]);

  const handleEnter = () => {
    const anonymousLogin = () => {
      return isAnonymous ? chatAPI.validateToken(getGlobal('REACT_APP_ACCESS_TOKEN'))
        .then(res => {
          setUser(res);
          let _chat: any = localStorage.getItem('_chat');
          if (_chat) {
            try {
              _chat = JSON.parse(_chat);

              if (_chat?.communicationTool?._id === getGlobal('REACT_APP_SYDLE_CORPORATE_ACCOUNT_ID') && _chat?.client?._id === res.code) {
                return chatAPI.getChat(_chat).catch(() => Promise.resolve());
              }
            } catch (e) { }
          }
        }) : Promise.reject();
    };

    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search.replace('?', ''));

    if (!isAnonymous || localStorage.getItem('explorer_user') || urlParams.get('accessToken')) {
      try {
        let user = JSON.parse(localStorage.getItem('explorer_user') as string);
        
        if (user && user.accessToken && user.accessToken.token && user.code !== SD_ANONYMOUS_USER_ID) {          
          let application = user.accessToken.payload?.audiency?.[0] || getGlobal('REACT_APP_APPLICATION');
          setGlobal('REACT_APP_APPLICATION', application);

          chatAPI.renewToken(user.accessToken.token, application)
            .then(res => {
              localStorage.setItem('explorer_user', JSON.stringify(res));
              setUser(res);
              let _chat = localStorage.getItem('_chat') as any;
              if (_chat) {
                try {
                  _chat = JSON.parse(_chat);
                  if (_chat?.communicationTool?._id === getGlobal('REACT_APP_SYDLE_CORPORATE_ACCOUNT_ID') && _chat?.client?._id === res.code) {
                    return chatAPI.getChat(_chat).catch(() => Promise.resolve());
                  }
                } catch (e) { }
              }
            })
            .catch(() => anonymousLogin())
            .catch(() => setUser(null!))
            .finally(() => setLoading(false));
        } else if (urlParams.get('accessToken')) {
          const token = urlParams.get('accessToken') || '';
          chatAPI.renewToken(token)
            .then(res => {
              localStorage.setItem('explorer_user', JSON.stringify(res));
              setUser(res);
              let _chat = localStorage.getItem('_chat') as any;
              if (_chat) {
                try {
                  _chat = JSON.parse(_chat);
                  if (_chat?.communicationTool?._id === getGlobal('REACT_APP_SYDLE_CORPORATE_ACCOUNT_ID') && _chat?.client?._id === res.code) {
                    return chatAPI.getChat(_chat).catch(() => Promise.resolve());
                  }
                } catch (e) { }
              }
            })
            .catch(() => anonymousLogin())
            .catch(() => setUser(null!))
            .finally(() => setLoading(false));
        } else {
          if (isAnonymous) {
            anonymousLogin()
              .catch(() => setUser(null!))
              .finally(() => setLoading(false));
          } else {
            setLoading(false);
            setUser(null!);
          }
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      anonymousLogin()
        .catch(() => setUser(null!))
        .finally(() => setLoading(false));
    }
  }

  useEffect(() => {
    const callback = (res: ChatType) => {
      if (res.communicationTool?.user?.photo) {
        chatAPI.downloadPhoto(res.communicationTool?.user?.photo).then(file => setAvatarURL(file.url));
      }

      setChat(res);
    };

    chatAPI.on('chat', callback);
    return () => {
      chatAPI.off('chat', callback);
    };
  }, [chatAPI, setChat, setAvatarURL]);

  return !isLoading ? (
    <StyledCollapse
      component="sdiv"
      horizontalPosition={theme.horizontalPosition}
      customPosition={theme.customPosition}
      collapsedHeight={`${isMobile || responsiveMode ? 65 : 50}px`}
      in={showChat}
      showChat={showChat}
      isExpanded={isExpanded}
      disableStrictModeCompat={true}
      onEnter={handleEnter}
      timeout={{ appear: 200, enter: 200, exit: 150 }}
      responsiveMode={responsiveMode}
    >
      <Container as="sdiv" isExpanded={isExpanded} responsiveMode={responsiveMode} showChat={showChat}>
        <ChatHeader forceResponsiveMode={responsiveMode} icon={showAvatar ? avatarURL : undefined} />
        <Content as="sdiv">
          {loading ?
            <LoadingChat as="sdiv" hasFooter={!loading && !!user} isExpanded={isExpanded} responsiveMode={responsiveMode}>
              <StartMessages as="sdiv">
                <MessageSkeleton component="sdiv" variant="rect" width={180} height={45} />
                <MessageSkeleton component="sdiv" variant="rect" width={190} height={45} />
              </StartMessages>
              <EndMessages as="sdiv">
                <MessageSkeleton component="sdiv" variant="rect" width={140} height={45} />
                <MessageSkeleton component="sdiv" variant="rect" width={160} height={45} />
              </EndMessages>
              <StartMessages as="sdiv">
                <MessageSkeleton component="sdiv" variant="rect" width={170} height={45} />
              </StartMessages>
              <EndMessages as="sdiv">
                <MessageSkeleton component="sdiv" variant="rect" width={140} height={45} />
                <MessageSkeleton component="sdiv" variant="rect" width={160} height={45} />
              </EndMessages>
            </LoadingChat>
            : user ?
              <ChatBody />
              :
              <Form />
          }
          {!loading && user && <ChatFooter />}
        </Content>
      </Container>
    </StyledCollapse>
  ) : null;
}

export default Chat;